import { type SelectEventObject } from "highcharts"
import { maxBy } from "lodash"

import { useState } from "react"

import { Card } from "~/components/reusable/Card"
import AreaGraphSilhouette from "~/components/reusable/skeletonSilhouette/AreaGraphSilhouette"
import useShowHelix from "~/hooks/useShowHelix"
import { useUser } from "~/providers/userProvider"
import { type DateRange } from "~/stores/dateRangeStore"
import { api } from "~/utils/api"
import { startOfTodayUTC, weeksInRange } from "~/utils/datetime"
import { roundForecast } from "~/utils/formatting"
import { selectedForecastWeekRange } from "~/utils/graphUtils"

import SuperChart, { type DateChartSeries, type PlotBand } from "../reusable/graphs/SuperChart"
import { ManageAdjustmentDialog } from "./dialogs/ManageAdjustmentDialog"
import { useChartSeriesConfig } from "~/constants/colors"
import { ChartType } from "~/constants/graphs"
import { formatName } from "~/utils/namingUtils"

export default function ForecastManyGraph({
  variantIds,
  firstDisplayDay,
  warehouseId,
  channels,
  allowAdjustments,
  height,
  adjustment,
  oldAdjustment,
}: {
  variantIds: string[]
  firstDisplayDay: Date
  warehouseId?: string
  channels?: string[]
  allowAdjustments?: boolean
  height?: number
  adjustment?: {
    startDate: Date
    endDate: Date
    type: string
    value: number
  }
  oldAdjustment?: {
    startDate: Date
    endDate: Date
    type: string
    value: number
  }
}) {
  const chartConfig = useChartSeriesConfig()
  const [openAdjustmentDialog, setOpenAdjustmentDialog] = useState(false)
  const [selectedGraphDateRange, setSelectedGraphDateRange] = useState<DateRange>()
  const { permissions } = useUser()
  const showHelix = useShowHelix()
  const { data: hasPreOrders } = api.preOrder.hasPreOrders.useQuery()

  const { data, isLoading } = api.product.salesAndForecasts.useQuery(
    {
      variantIds,
      warehouseId,
      channels,
      startDate: firstDisplayDay,
    },
    { enabled: variantIds.length > 0 },
  ) // TODO: Use hook instead

  if (variantIds.length === 0) {
    return (
      <Card className="flex justify-center">
        <p className="text-sm italic">No SKUs selected</p>
      </Card>
    )
  }

  if (isLoading || !data) {
    return (
      <div style={{ height: height ?? 400 }} className="w-full">
        <AreaGraphSilhouette />
      </div>
    )
  }

  function forecastGraphRangeSelected(event: SelectEventObject): boolean {
    const range = selectedForecastWeekRange(event)

    if (range) {
      setSelectedGraphDateRange(range)
      setOpenAdjustmentDialog(true)
    }

    return false
  }

  const salesGraphData: DateChartSeries = {
    name: formatName("sale", true, true),
    data: data["sales"].map(({ date, value }) => ({
      milliseconds: date.getTime(),
      value,
    })),
    type: "areaspline",
    colors: [chartConfig.sales.color],
    dashStyle: chartConfig.sales.dashStyle,
  }

  const forecastGraphData: DateChartSeries = {
    name: "Demand",
    data: data["forecasts"].map(({ date, value }) => {
      // There is some edge cases with multiple adjustments
      if (oldAdjustment && date >= oldAdjustment.startDate && date <= oldAdjustment.endDate) {
        if (oldAdjustment.type === "add") {
          value = value - oldAdjustment.value
        }
        if (oldAdjustment.type === "percentage") {
          if (oldAdjustment.value <= -100) {
            value =
              data["rawForecasts"].find((f) => f.date.getTime() === date.getTime())?.value ?? 0
          } else {
            value = value / (1 + oldAdjustment.value / 100)
          }
        }
        if (oldAdjustment.type === "set") {
          value = data["rawForecasts"].find((f) => f.date.getTime() === date.getTime())?.value ?? 0
        }
      }
      if (adjustment && date >= adjustment.startDate && date <= adjustment.endDate) {
        if (adjustment.type === "add") {
          value = value + adjustment.value
        }
        if (adjustment.type === "percentage") {
          value = value * (1 + adjustment.value / 100)
        }
        if (adjustment.type === "set") {
          value = adjustment.value
        }
        if (adjustment.type === "setTotal") {
          value = Math.round(
            adjustment.value / weeksInRange(adjustment.startDate, adjustment.endDate),
          )
        }
      }
      value = Math.max(value, 0)
      return {
        milliseconds: date.getTime(),
        value: roundForecast(value),
      }
    }),
    type: "areaspline",
    colors: [chartConfig.demand.color],
    dashStyle: chartConfig.demand.dashStyle,
  }

  const rawForecastGraphData: DateChartSeries = {
    name: "Raw Demand",
    data: data["rawForecasts"].map(({ date, value }) => ({
      milliseconds: date.getTime(),
      value: roundForecast(value),
    })),
    type: "spline",
    colors: [chartConfig.rawDemand.color],
    dashStyle: chartConfig.rawDemand.dashStyle,
  }

  const knownPreOrders: DateChartSeries = {
    name: `Known ${formatName("preorder", true, true)}`,
    data: data["preOrders"].map(({ date, value }) => ({
      milliseconds: date.getTime(),
      value,
    })),
    type: "column",
    colors: [chartConfig.preOrder.color],
    dashStyle: chartConfig.preOrder.dashStyle,
    hidden: true,
  }
  const plotData = [salesGraphData, forecastGraphData, ...(showHelix ? [] : [rawForecastGraphData])]
  if (hasPreOrders) {
    plotData.push(knownPreOrders)
  }

  const adjustmentPlotBands: PlotBand[] = adjustment
    ? [
        {
          from: adjustment.startDate.getTime(),
          to: adjustment.endDate.getTime(),
          color: "rgba(147, 231, 110, 0.12)",
          borderColor: "rgba(147, 231, 110, 0.5)",
          text: "Adjustment period",
          textColor: "rgb(107, 191, 70)",
        },
      ]
    : []

  const endDate = maxBy(data["forecasts"], "date")?.date ?? startOfTodayUTC()
  return (
    <>
      <SuperChart
        type={ChartType.TimeSeries}
        height={height ?? 400}
        data={plotData}
        dateRange={{
          start: firstDisplayDay,
          end: endDate,
        }}
        plotBands={adjustmentPlotBands.length > 0 ? { x: adjustmentPlotBands } : undefined}
        selectionEvent={
          permissions.canEdit && allowAdjustments ? forecastGraphRangeSelected : undefined
        }
      />
      {permissions.canEdit && allowAdjustments && (
        <ManageAdjustmentDialog
          key={`${selectedGraphDateRange?.start?.toISOString()}-${selectedGraphDateRange?.end?.toISOString()}`}
          variantIds={variantIds}
          customTrigger={<div />}
          customOpen={openAdjustmentDialog}
          onClose={() => {
            setSelectedGraphDateRange(undefined)
            setOpenAdjustmentDialog(false)
          }}
          presetStartDate={selectedGraphDateRange?.start}
          presetEndDate={selectedGraphDateRange?.end}
        />
      )}
    </>
  )
}
